import { useEffect, useMemo, useState } from 'react';
import { VStack } from '@chakra-ui/react';
import { ContentLayout, Loader } from 'Molecules';
import { MaterialityHeader } from './MaterialityUtils';
import { AssessStandardMateriality } from './DoubleMaterialitySteps/AssessStandardMateriality';
import { ProvideReasoning } from './DoubleMaterialitySteps/ProvideReasoning';
import { AssessDisclosureRequirements } from './DoubleMaterialitySteps/AssessDisclosureRequirements';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetStandardMandatoryMetricsQuery,
  useGetStandardNonMandatoryMetricsQuery,
  useUpdateAssessmentOnboardingMutation,
} from 'models';
import {
  getParentStandardMateriality,
  getStandardMateriality,
  useAddMateriality,
  useGetStandardMaterialityData,
} from './DoubleMaterialityAssessment.hooks';
import { AdditionalType, StandardMaterialityState } from './DoubleMaterialityAssessment.d';
import { isParentMaterial } from '../MaterialityAssessment.hooks';
import { FrameworkRefs } from '../../Assessment';

export const DoubleMaterialityAssessment = ({ onboarding = false }: { onboarding?: boolean }) => {
  const [step, setStep] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { standardRef = '', esrsAssessmentId = '', companyId = '', framework = '' } = useParams();
  const addMateriality = useAddMateriality();

  const { data: mandatoryMetrics, loading: mandatoryMetricsLoading } =
    useGetStandardMandatoryMetricsQuery({
      fetchPolicy: 'network-only',
      variables: {
        standardRef,
        esrsAssessmentId,
      },
      skip: !standardRef || !esrsAssessmentId,
    });
  const { data: nonMandatoryMetrics, loading: nonMandatoryMetricsLoading } =
    useGetStandardNonMandatoryMetricsQuery({
      fetchPolicy: 'network-only',
      variables: {
        standardRef,
        esrsAssessmentId,
      },
      skip: !standardRef || !esrsAssessmentId,
    });

  const {
    standard,
    materialStandard,
    parentStandardMaterialityData,
    hasParent,
    isGroupOwner,
    parentData,
    isCollectOnly,
    loading,
  } = useGetStandardMaterialityData();

  const navigate = useNavigate();

  const [updateOnboardingStep] = useUpdateAssessmentOnboardingMutation();

  const isStandardRequiredByParent = useMemo(
    () =>
      parentStandardMaterialityData?.isMaterial &&
      isParentMaterial(parentStandardMaterialityData.materialMetrics),
    [materialStandard, parentStandardMaterialityData]
  );

  const hasPhasedInOption = useMemo(
    () =>
      standard?.additionalTypes?.some((additionalType) =>
        additionalType?.additionalType?.title?.includes('Phased')
      ),
    [standard]
  );

  const [isCollectOnlyMandatory, isCollectData] = useMemo(
    () => [
      isCollectOnly && isStandardRequiredByParent,
      materialStandard?.isMaterial && isCollectOnly,
    ],
    [materialStandard, isCollectOnly, isStandardRequiredByParent]
  );

  const isStandardMandatory = useMemo(() => {
    const isTopicalStandardNotMandatory =
      isCollectOnly && !isStandardRequiredByParent && !standard?.isTopical;
    if (isTopicalStandardNotMandatory) return false;
    return !(standard?.isTopical ?? false);
  }, [isCollectOnly, isStandardRequiredByParent, standard]);

  const standardMateriality = useMemo(
    () =>
      getStandardMateriality(
        isCollectOnlyMandatory ?? false,
        isCollectData ?? false,
        isCollectOnly,
        isStandardMandatory,
        materialStandard
      ),
    [materialStandard, isCollectOnly, isCollectOnlyMandatory, isCollectData, isStandardMandatory]
  );

  const parentMateriality = useMemo(
    () =>
      getParentStandardMateriality(
        !!parentStandardMaterialityData,
        parentStandardMaterialityData?.isMaterial,
        parentStandardMaterialityData?.isPhasedIn
      ),
    [parentStandardMaterialityData]
  );

  const standardAdditionalTypes: AdditionalType[] = useMemo(() => {
    return (
      standard?.additionalTypes
        .map((aType) => aType.additionalType)
        .map((type) => ({
          reference: type.reference,
          title: type.title ?? '',
          description: type.description ?? '',
          learnMore: type.learnMore ?? '',
        })) ?? []
    );
  }, [standard]);

  const showStandardMateriality = useMemo(
    () => !isStandardMandatory && !isCollectOnlyMandatory,
    [isStandardMandatory, isCollectOnlyMandatory]
  );

  const showDrTable = useMemo(
    () => (isStandardMandatory || isCollectOnlyMandatory || materialStandard?.isMaterial) ?? false,
    [isStandardMandatory, materialStandard]
  );

  const onDoneClick = () => {
    if (onboarding) {
      updateOnboardingStep({
        variables: {
          id: esrsAssessmentId,
          onboardingStep: 3,
        },
      });
      navigate(
        `/${companyId}/sustainability-reporting/${framework}/${esrsAssessmentId}/onboarding`
      );
    } else {
      navigate(`/${companyId}/sustainability-reporting/${framework}/${esrsAssessmentId}/settings`);
    }
  };

  useEffect(() => {
    if (materialStandard?.isMaterial && isInitialLoad) setStep(2);
  }, [materialStandard, isInitialLoad]);

  const metrics = useMemo(
    () => [
      ...(mandatoryMetrics?.EsrsMetric ?? []),
      ...(standardMateriality === StandardMaterialityState.phasedIn
        ? (nonMandatoryMetrics?.EsrsMetric ?? [])
        : []),
    ],
    [mandatoryMetrics, nonMandatoryMetrics, standardMateriality]
  );

  useEffect(() => {
    if (
      !loading &&
      standardMateriality === StandardMaterialityState.collectDataMandatory &&
      !materialStandard
    ) {
      addMateriality(
        StandardMaterialityState.collectDataMandatory,
        mandatoryMetrics?.EsrsMetric ?? [],
        false,
        parentStandardMaterialityData,
        isCollectOnly
      );
    }
  }, [loading, standardMateriality, materialStandard, mandatoryMetrics]);

  if (loading || mandatoryMetricsLoading || nonMandatoryMetricsLoading) {
    return <Loader />;
  }

  return (
    <ContentLayout backButton={true} header={false}>
      <VStack
        spacing="16px"
        alignItems="stretch"
        width="98%"
        mb="14px"
        bg="bg.default"
        padding="24px 24px 72px 24px"
        border="1px solid"
        borderColor="border.decorative"
        borderRadius="8px"
        position="relative"
        gap="0px"
        maxWidth="1560px"
      >
        <MaterialityHeader
          title={standard?.title ?? ''}
          reference={framework === FrameworkRefs.vsme ? '' : (standard?.reference ?? '')}
          materialStandard={materialStandard}
          isCollectOnly={isCollectOnly}
          hasParent={hasParent}
          standardAdditionalTypes={standardAdditionalTypes}
          parentMateriality={parentMateriality}
          materiality={standardMateriality}
          isStandardMandatory={isStandardMandatory}
          isRequiredByParent={isStandardRequiredByParent ?? false}
        />
        {showStandardMateriality && (
          <>
            <AssessStandardMateriality
              step={step}
              setStep={setStep}
              isCollectOnly={isCollectOnly ?? false}
              standardMateriality={standardMateriality}
              isGroup={isGroupOwner}
              setIsInitialLoad={setIsInitialLoad}
              isParentMaterial={isStandardRequiredByParent ?? false}
              hasPhasedInOption={hasPhasedInOption}
              materialStandardId={materialStandard?.id}
              metrics={metrics}
            />
            <ProvideReasoning
              step={step}
              setStep={setStep}
              onDoneClick={onDoneClick}
              materialStandard={materialStandard}
              showDrTable={showDrTable}
            />
          </>
        )}
        {showDrTable && (
          <AssessDisclosureRequirements
            step={step}
            hasParent={hasParent}
            materialStandard={materialStandard}
            isGroup={isGroupOwner}
            onDoneClick={onDoneClick}
            standardMateriality={standardMateriality}
            parentStandardMaterialityData={parentStandardMaterialityData}
            parentData={parentData}
            isCollectOnly={isCollectOnly}
            standard={standard}
          />
        )}
      </VStack>
    </ContentLayout>
  );
};
